import { useState, type ReactNode } from 'react';

export type TabsSize = 'sm' | 'md';

export interface TabItem<V extends string = string> {
  value: V;
  label: string;
  count?: number;
  icon?: ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

export interface TabsProps<V extends string = string> {
  tabs: Array<TabItem<V>>;
  value: V;
  onChange: (value: V) => void;
  size?: TabsSize;
  fullWidth?: boolean;
  noBorder?: boolean;
}

// ── Sizing ────────────────────────────────────────────────────────────────────

const H:   Record<TabsSize, string> = { sm: 'var(--evo-tab-h)', md: 'var(--evo-tab-h)' };
const FS:  Record<TabsSize, string> = { sm: 'var(--evo-tab-fs)', md: 'var(--evo-tab-fs)' };
const PX:  Record<TabsSize, string> = { sm: 'var(--evo-tab-px)', md: 'var(--evo-tab-px)' };
const GAP: Record<TabsSize, number> = { sm: 6,      md: 8      };

// ── Tab ───────────────────────────────────────────────────────────────────────

function Tab<V extends string>({
  item,
  active,
  size,
  fullWidth,
  onClick,
}: {
  item: TabItem<V>;
  active: boolean;
  size: TabsSize;
  fullWidth: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const inactive = !!item.disabled;

  let color = 'var(--evo-fg-3)';
  if (active) color = 'var(--evo-cyan)';
  else if (hovered && !inactive) color = 'var(--evo-fg-1)';

  const borderColor = active ? 'var(--evo-cyan)' : 'transparent';

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={inactive}
      title={inactive && item.tooltip ? item.tooltip : undefined}
      onClick={!inactive ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: GAP[size],
        height: H[size],
        padding: `0 ${PX[size]}`,
        fontSize: FS[size],
        fontWeight: active ? 500 : 400,
        fontFamily: 'var(--evo-font)',
        lineHeight: 1,
        background: 'transparent',
        color,
        border: 'none',
        borderBottom: `var(--evo-tab-underline-bw) solid ${borderColor}`,
        marginBottom: '-1px',
        cursor: inactive ? 'not-allowed' : 'pointer',
        opacity: inactive ? 0.5 : 1,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        flex: fullWidth ? 1 : 'none',
        transition: 'color 120ms linear, border-color 120ms linear',
        boxSizing: 'border-box',
        outline: 'none',
      }}
    >
      {item.icon != null && (
        <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>{item.icon}</span>
      )}
      <span>{item.label}</span>
      {item.count != null && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 18,
            height: 16,
            padding: '0 5px',
            fontSize: '10px',
            fontWeight: 500,
            lineHeight: 1,
            color: active ? 'var(--evo-fg-on-cyan)' : 'var(--evo-fg-3)',
            background: active ? 'var(--evo-cyan)' : 'var(--evo-bg-3)',
            borderRadius: 'var(--evo-r-pill)',
            boxSizing: 'border-box',
          }}
        >
          {item.count}
        </span>
      )}
    </button>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

export function Tabs<V extends string = string>({
  tabs,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
  noBorder = false,
}: TabsProps<V>) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        borderBottom: noBorder ? 'none' : '1px solid var(--evo-stroke)',
        width: fullWidth ? '100%' : 'auto',
        boxSizing: 'border-box',
      }}
    >
      {tabs.map(t => (
        <Tab
          key={t.value}
          item={t}
          active={t.value === value}
          size={size}
          fullWidth={fullWidth}
          onClick={() => onChange(t.value)}
        />
      ))}
    </div>
  );
}
